import { getApiBaseUrl } from "@/lib/urls";

// Simple resolver to a stable server-served URL. Avoids glob import that broke Vite dep-scan.
// Icons are served by the backend at /chart_icons/* via express.static.
// In production builds (Vercel), icons are copied to public/chart_icons and served directly.
// This function prefers relative paths (served from Vercel CDN) over API URLs for better performance.
export function resolveIconSrc(iconFile: string): string {
  const clean = String(iconFile || '').replace(/^\/+/, '');
  
  // In production builds, prefer relative paths (served from Vercel's CDN)
  // This is faster and more reliable than proxying through the API
  const isProduction = import.meta.env.PROD || import.meta.env.VERCEL === '1';
  
  if (isProduction) {
    // Use relative path - Vercel will serve from public/chart_icons
    return `/chart_icons/${clean}`;
  }
  
  // In development, ALWAYS use API URL to avoid Vite serving stale files from public folder
  // Vite serves public folder files before checking proxy rules, so we must use full API URL
  // Add cache-busting timestamp to force browser to reload updated icons
  try {
    const base = getApiBaseUrl();
    if (base && base.trim().length > 0) {
      // Add cache-busting query param in development to force reload of updated icons
      // Use a combination of timestamp and random to ensure fresh loads
      // This ensures updated icons are always loaded fresh in development
      // Use sessionStorage to track reloads and force cache invalidation
      const sessionKey = `icon_cache_${clean}`;
      const lastLoad = sessionStorage.getItem(sessionKey);
      const now = Date.now();
      // Force reload if more than 1 second has passed or if this is first load
      const forceReload = !lastLoad || (now - parseInt(lastLoad, 10)) > 1000;
      if (forceReload) {
        sessionStorage.setItem(sessionKey, now.toString());
      }
      const cacheBuster = `?v=${now}&r=${Math.random().toString(36).substring(7)}&_=${forceReload ? '1' : '0'}`;
      const fullUrl = `${base.replace(/\/$/, '')}/chart_icons/${clean}${cacheBuster}`;
      // Debug logging in development
      if (!isProduction && (clean.includes('bullet') || clean.includes('hexbin'))) {
        console.log(`[iconMap] Resolved ${clean} icon URL:`, fullUrl);
      }
      return fullUrl;
    }
  } catch (e) {
    console.error('[iconMap] Error getting API base URL:', e);
  }
  
  // Fallback: if API URL is not available, still try relative but warn
  // This should rarely happen in development
  console.warn('[iconMap] API base URL not available, using relative path (may serve stale files)');
  return `/chart_icons/${clean}`;
}


